import { call, debounce, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import i18n from '../../i18n/i18n';
import {
	ChatMessage,
	ChatMessagesPage,
	ConversationSummary,
	ConversationWithPlayer,
	getChatUnreadCount,
	getConversationMessages,
	getConversations,
	getConversationWithPlayer,
	markConversationRead,
	sendConversationMessage,
} from '../../services/chatApi';
import { cleverTapService } from '../../services/clevertap/clevertap.service';
import {
	chatConversationsFailure,
	chatConversationsRequest,
	chatConversationsSuccess,
	chatMarkReadRequest,
	chatMessageReceived,
	chatMessagesFailure,
	chatMessagesRequest,
	chatMessagesSuccess,
	chatResolveFailure,
	chatResolveRequest,
	chatResolveSuccess,
	chatSendFailure,
	chatSendRequest,
	chatSendSuccess,
	chatUnlocked,
	chatUnreadCountRequest,
	chatUnreadCountSuccess,
} from './chatActions';
import type { ChatState } from './chatReducer';

type WithChat = { chat: ChatState };

/** Messages arriving in an open thread mark it read once they settle, not once per message. */
const MARK_READ_DEBOUNCE_MS = 400;

/** Translated when the error is built, never at module load. */
const loadFailedMessage = () =>
	i18n.t('T_SITE_CHAT_LOAD_FAILED', {
		defaultValue: 'Could not load the chat. Please try again.',
	});

/** 403: one of the two blocked the other, or the sender may no longer chat. A retry cannot help. */
const SEND_REFUSED = 403;

/** The chat service answers a refused send with a status the player can act on. */
const sendErrorMessage = (error: any): string => {
	const status = error?.response?.status;
	if (status === 429) {
		return i18n.t('T_SITE_CHAT_RATE_LIMITED', {
			defaultValue: 'You are sending messages too fast. Wait a moment and try again.',
		});
	}
	if (status === SEND_REFUSED) {
		return i18n.t('T_SITE_CHAT_SEND_FORBIDDEN', {
			defaultValue: 'You can no longer send messages to this user.',
		});
	}
	return i18n.t('T_SITE_CHAT_SEND_FAILED', { defaultValue: 'Message not sent.' });
};

function* fetchConversationsWorker() {
	try {
		const response: { items: ConversationSummary[] } = yield call(getConversations);
		yield put(chatConversationsSuccess({ items: response?.items ?? [] }));
	} catch {
		yield put(chatConversationsFailure(loadFailedMessage()));
	}
}

function* fetchUnreadCountWorker() {
	try {
		const response: { count: number } = yield call(getChatUnreadCount);
		yield put(chatUnreadCountSuccess({ count: Number(response?.count) || 0 }));
	} catch {
		// The dot simply stays as it was; the next conversations fetch corrects it.
	}
}

function* resolveWorker(action: ReturnType<typeof chatResolveRequest>) {
	const { playerId } = action.payload;
	try {
		const response: ConversationWithPlayer = yield call(getConversationWithPlayer, playerId);
		yield put(
			chatResolveSuccess({
				playerId,
				conversationId: response?.conversationId ?? null,
				unlocked: !!response?.unlocked && !!response?.conversationId,
			}),
		);
	} catch {
		yield put(chatResolveFailure({ playerId, message: loadFailedMessage() }));
	}
}

function* fetchMessagesWorker(action: ReturnType<typeof chatMessagesRequest>) {
	const { conversationId, older = false } = action.payload;
	try {
		const oldest: ChatMessage | undefined = older
			? yield select((s: WithChat) =>
					s.chat.threads[conversationId]?.items.find(message => !message.status),
				)
			: undefined;
		const page: ChatMessagesPage = yield call(getConversationMessages, conversationId, {
			before: oldest?.createdAt,
		});
		yield put(
			chatMessagesSuccess({
				conversationId,
				items: page?.items ?? [],
				hasMore: !!page?.hasMore,
				older,
			}),
		);
	} catch {
		yield put(chatMessagesFailure({ conversationId, older, message: loadFailedMessage() }));
	}
}

function* sendWorker(action: ReturnType<typeof chatSendRequest>) {
	const { conversationId, clientId, text, senderId, recipientId } = action.payload;
	try {
		const message: ChatMessage = yield call(sendConversationMessage, conversationId, text);
		yield put(chatSendSuccess({ conversationId, clientId, message }));
		// Never the text itself.
		cleverTapService.trackEvent('private_message_sent', {
			conversation_id: conversationId,
			sender_id: senderId,
			recipient_id: recipientId,
		});
	} catch (error) {
		yield put(
			chatSendFailure({
				conversationId,
				clientId,
				message: sendErrorMessage(error),
				retryable: (error as any)?.response?.status !== SEND_REFUSED,
			}),
		);
	}
}

function* markReadWorker(action: ReturnType<typeof chatMarkReadRequest>) {
	try {
		yield call(markConversationRead, action.payload.conversationId);
	} catch {
		// Harmless: the conversation shows unread again on the next fetch, and is re-marked on open.
	}
}

/** A message or unlock for a conversation the list has not seen yet: fetch the list again. */
function* refreshUnknownConversation(
	action: ReturnType<typeof chatMessageReceived> | ReturnType<typeof chatUnlocked>,
) {
	const conversationId =
		'message' in action.payload
			? action.payload.message.conversationId
			: action.payload.conversationId;
	const known: boolean = yield select((s: WithChat) =>
		s.chat.conversations.items.some(c => c.id === conversationId && c.otherPlayer?.username),
	);
	const loading: boolean = yield select((s: WithChat) => s.chat.conversations.loading);
	if (!known && !loading) yield put(chatConversationsRequest());
}

export function* chatRootSaga() {
	yield takeLatest(chatConversationsRequest.type, fetchConversationsWorker);
	yield takeLatest(chatUnreadCountRequest.type, fetchUnreadCountWorker);
	yield takeEvery(chatResolveRequest.type, resolveWorker);
	yield takeEvery(chatMessagesRequest.type, fetchMessagesWorker);
	yield takeEvery(chatSendRequest.type, sendWorker);
	yield debounce(MARK_READ_DEBOUNCE_MS, chatMarkReadRequest.type, markReadWorker);
	yield takeEvery([chatMessageReceived.type, chatUnlocked.type], refreshUnknownConversation);
}
